import React, { useState } from "react";
import { DrawerComponent } from "@comps/components";
import { makeRequest, notify } from "@utils/helpers";
import { createCaseReq } from "../requests";
import CaseJobForm from "./CaseJobForm";
import { checkUploadSize } from "@utils/mediaFormats";

// The file slots have no Form.Item to hang a message on, so a
// server-side rejection there would otherwise vanish silently. `files`
// is the whole-upload case: PHP refuses a request over post_max_size
// before Laravel routes it, and the handler reports it under that key.
const FILE_SLOTS = ['photos', 'xray', 'scans', 'files'];

const fileSlotErrors = (data) =>
  FILE_SLOTS.flatMap((slot) => (data && data[slot]) || []);

// Every message a validation failure (422) carries. The form shows them
// under their fields, but the field can be on another step of the form,
// so the notice says them too, instead of just "Validation Error".
const validationMessages = (data, res) =>
  res?.code === 422 && data && typeof data === 'object'
    ? [...new Set(Object.values(data).flat().filter((m) => typeof m === 'string'))]
    : [];

const formName = "caseStatuss";
const CreateCase = (props) => {
  const [loader, setLoader] = useState(false);
  const [errors, setErrors] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [xray, setXray] = useState([]);
  const [scans, setScans] = useState([]);

  const onFinish = (data) => {
    // DatePicker hands back a dayjs value, and moment() can't read one —
    // it silently fell back to "now", so every case was created with
    // today's date as the patient's date of birth.
    data.dob = data.dob ? data.dob.format("YYYY-MM-DD") : null;
    if (scans.length === 0) {
      notify("Error", "Please upload scans", "error");
      return;
    }
    /*
     * PHP rejects a request over post_max_size before Laravel routes it,
     * so an oversized batch never reached the code that would have
     * reported it — the screen could only say "Case not created". Catch
     * it here, while the reader can still see which files are at fault.
     */
    const tooLarge = checkUploadSize({ photos, xray, scans });
    if (tooLarge) {
      notify(tooLarge.title, tooLarge.description, "error");
      return;
    }
    let payload = {
      "object": {
        ...data,
        photos: photos,
        xray: xray,
        scans: scans
      }
    }
    // debugger
    makeRequest(setLoader, createCaseReq, payload, onSuccess, onError);
  }

  const onSuccess = (data, res) => {
    // "CaseStatus Created" was scaffold copy left on the case form — say
    // what was actually created, and which one.
    notify("Case created", data.object?.case_number
      ? `Case ${data.object.case_number} is now in New Cases.`
      : res.msg);
    props.onCreated(data.object);
  }

  /*
   * `res` matters as much as `data` here. This used to take only the
   * field errors, so a failure carrying no field at all — an upload
   * over the server's size limit, a timeout — set no message anywhere
   * and the drawer just sat there saying nothing, which is what "Case
   * not created, no reason given" looked like.
   */
  const onError = (data, res) => {
    // A failure that never reached the app (no connection, a refused
    // upload) carries no field errors at all, and the form reads this by
    // field name — handing it null took the whole page down.
    setErrors(data && typeof data === 'object' ? data : []);

    const rejected = fileSlotErrors(data);
    if (rejected.length) {
      notify("Those files weren't accepted", rejected.join(' '), "error");
      return;
    }

    const messages = validationMessages(data, res);
    notify("Case not saved", messages.length
      ? messages.join(' ')
      : res?.msg || "Something went wrong. Please try again.", "error");
  }

  return (
    <DrawerComponent width={ 800 } mainTitle="Create" subTitle="Case" open={ true } footer={ false } onClose={ () => props.onClosed(false) }>
      <CaseJobForm setLoader={ setLoader } loader={ loader } setErrors={ setErrors } errors={ errors } onFinish={ onFinish } formName={ formName } onClosed={ props.onClosed } setPhotos={ setPhotos }
        setXray={ setXray } setScans={ setScans }
      />
    </DrawerComponent>
  );
}

export default CreateCase
